import React, { FormEvent, useCallback, useMemo, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { useNavigate } from 'react-router-dom';
import moment from 'moment-timezone';
import { Trans, useTranslation } from 'react-i18next';
import { Colors, Theme } from '@app/theme';

import {
  Consent,
  ConsentName,
  PlanStatus,
  SelectionBehavior,
  Session,
  SessionStatus,
  useAcceptConsent,
  useAcceptPlan,
  useForm,
  useIncentiveText,
  useMarkStudyStepAsValidated,
  useParticipantFieldsByName,
  usePlan,
  usePublishSession,
  useRecruitment,
  useTargetingPlanOffer,
  useTaskSetting,
} from '@app/api';
import { CenteredLoader, Chip, ConsentCheckbox, useToast } from 'components';

import { useGetParticpantFieldCriterionTextValue } from 'lib/criteriaUtils';

import { SessionReviewSection } from './components/SessionReviewSection';
import { ReviewPlanCard } from './components/ReviewPlanCard';
import { SessionFormSection } from './components/SessionFormSection';
import { EditSessionLayout } from './components/EditSessionLayout';
import { SessionSubmitLine } from './components/SessionSubmitLine';
import { EditSessionStep } from './steps';
import { UpsellSupportReviewSection } from './components/UpsellSupportReviewSection';

const smallScreen = '@media (max-width: 1250px)';

const useStyles = createUseStyles((theme: Theme) => ({
  contentClassName: {
    maxWidth: '100%',
    paddingLeft: theme.marginBase * 7,
    paddingRight: theme.marginBase * 7,
  },
  upsell: {
    marginTop: theme.marginBase * 4,
  },
  page: {
    display: 'flex',
    [smallScreen]: {
      flexDirection: 'column',
    },
  },
  summary: {
    flex: 1,
    minWidth: theme.maxContentWidth.reducedFormContent,
    maxWidth: theme.maxContentWidth.largeForm,
    width: '100%',
    margin: 'auto',
    [theme.media.tablet]: {
      maxWidth: 'none',
    },
  },
  summaryContent: {
    marginTop: theme.marginBase * 4,
  },
  planColumn: {
    position: 'relative',
    width: theme.marginBase * 46,

    marginLeft: theme.marginBase * 7,
    [smallScreen]: {
      marginLeft: 0,
      width: '100%',
    },
  },
  planCard: {
    position: 'sticky',
    top: theme.marginBase * 5,
    [smallScreen]: {
      position: 'relative',
      top: 0,
    },
  },
  empty: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
  },
  checkbox: {
    marginTop: theme.marginBase,
  },
  iconAndText: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.marginBase + 'px',
  },
}));

interface Props {
  session: Session;
}

export const EditSessionReview = ({ session }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();

  const getParticpantFieldCriterionTextValue =
    useGetParticpantFieldCriterionTextValue();

  const participantFieldsByName = useParticipantFieldsByName();

  const { data: plan, isLoading: quoteLoading } = usePlan(session.planId);
  const offer = useTargetingPlanOffer(plan && plan.id);

  const { data: recruitment, isLoading: recruitmentLoading } = useRecruitment(
    session.recruitmentId,
  );

  const { data: form, isLoading: formIsLoading } = useForm(
    recruitment && recruitment.screeningFormId,
  );

  const { data: taskSetting, isLoading: taskSettingIsLoading } = useTaskSetting(
    session.taskSettingId,
  );
  const moderatedTaskSettings =
    taskSetting && 'format' in taskSetting ? taskSetting : undefined;

  const unmoderatedTaskSettings =
    taskSetting && 'finishedCode' in taskSetting ? taskSetting : undefined;

  const incentiveText = useIncentiveText(session.id, session, session.locale);

  const [isBillingInformationRequired, setIsBillingInformationRequired] =
    useState(false);
  const { mutateAsync: acceptPlan } = useAcceptPlan(session.planId);
  const { mutateAsync: acceptConsent } = useAcceptConsent();
  const [consents, setConsents] = useState<Consent[] | undefined>(undefined);
  const showConsent = plan && plan.status === PlanStatus.DRAFT;

  const { mutate: publish, isLoading: publishing } = usePublishSession(
    session.id,
    {
      onSuccess: () => {
        toast.success(t('toast.session.published'));
        navigate(`/sessions/details/${session.id}`);
      },
      onError(e: any) {
        if (!e.response || !e.response.data) {
          return toast.exception(e);
        }

        if (e.response.data.code === 'VALIDATION_FAILED') {
          toast.error(t('editSession.review.errors'));
          return;
        } else if (e.response.data.code === 'INCOMPLETE_BILLING_INFO') {
          setIsBillingInformationRequired(true);
        }
        toast.exception(e);
      },
    },
  );

  const fieldCriteria = useMemo(() => {
    if (!recruitment || !recruitment.criteria) {
      return [];
    }
    return recruitment.criteria.filter((criterion) => {
      return !!participantFieldsByName[criterion.fieldRef];
    });
  }, [participantFieldsByName, recruitment]);

  const questionCount = form?.fields?.length || 0;

  const isLoading =
    recruitmentLoading || quoteLoading || formIsLoading || taskSettingIsLoading;

  const { mutateAsync: markStepAsValidated } = useMarkStudyStepAsValidated(
    session.id,
  );
  const [submitted, setSubmitted] = useState(false);
  const submit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setSubmitted(true);
      if (showConsent) {
        if (!consents || !consents.length) {
          toast.error(t('form.errors.cgu.invalid'));
          return;
        }
        const consent = await acceptConsent(consents[0]);
        if (!consent.id) {
          toast.exception();
          return;
        }
        await acceptPlan({ cgsContentId: consent.id });
      }
      await markStepAsValidated({ step: 'review' });
      publish();
    },
    [
      publish,
      acceptPlan,
      showConsent,
      consents,
      setSubmitted,
      toast,
      t,
      markStepAsValidated,
    ],
  );

  return (
    <form onSubmit={submit}>
      <EditSessionLayout
        session={session}
        contentClassName={classes.contentClassName}
        footer={
          session.status === SessionStatus.DRAFT && (
            <SessionSubmitLine
              hidePrices
              session={session}
              saving={publishing}
              publish
            />
          )
        }
      >
        <div className={classes.page}>
          <div className={classes.summary}>
            <SessionFormSection title={t('editSession.review.title')}>
              <UpsellSupportReviewSection
                sessionId={session.id}
                className={classes.upsell}
              />
              {isLoading || !recruitment ? (
                <CenteredLoader />
              ) : (
                <div className={classes.summaryContent}>
                  <SessionReviewSection
                    step={EditSessionStep.SETTINGS}
                    session={session}
                  >
                    <SessionReviewSection.Element
                      label={t('sessions.display.internalName')}
                      value={session.internalName}
                    />
                    <SessionReviewSection.Element
                      label={t('sessions.display.numberOfParticipantRequired')}
                      value={String(session.numberOfParticipantRequired)}
                    />
                    <SessionReviewSection.Element
                      label={t('editSession.settings.videoCallProvider')}
                      hidden={!moderatedTaskSettings?.videoCallProvider}
                    >
                      {t(
                        `editSession.settings.videoCallProviderOptions.${moderatedTaskSettings?.videoCallProvider}`,
                      )}
                    </SessionReviewSection.Element>
                    <SessionReviewSection.Element
                      label={t('sessions.display.videoCallLink')}
                      value={moderatedTaskSettings?.videoCallLink}
                    />
                    <SessionReviewSection.Element
                      label={t('sessions.display.videoCallDetails')}
                      value={moderatedTaskSettings?.videoCallDetails}
                    />
                    <SessionReviewSection.Element
                      label={t('sessions.display.address')}
                      value={moderatedTaskSettings?.address}
                    />
                    <SessionReviewSection.Element
                      label={t('sessions.display.addressDetails')}
                      value={moderatedTaskSettings?.addressDetails}
                    />
                  </SessionReviewSection>

                  <SessionReviewSection
                    step={EditSessionStep.PUBLIC}
                    session={session}
                  >
                    <SessionReviewSection.Element
                      label={t('sessions.display.title')}
                      value={session.title}
                    />
                    <SessionReviewSection.Element
                      label={t('sessions.display.description')}
                      value={session.description}
                    />
                    <SessionReviewSection.Element
                      label={t('sessions.display.duration', {
                        context: session.studyType,
                      })}
                    >
                      {session.durationDisplayed ? (
                        <span>
                          {t('studySetup.generalSetup.duration.minutes', {
                            value: session.durationDisplayed,
                          })}
                        </span>
                      ) : (
                        <Trans
                          i18nKey="sessions.enums.duration.min"
                          values={{
                            count: session.duration,
                          }}
                        />
                      )}
                    </SessionReviewSection.Element>
                    <SessionReviewSection.Element
                      label={t('sessions.display.incentiveText')}
                      value={incentiveText}
                    />
                    <SessionReviewSection.Element
                      label={t('sessions.display.locale')}
                      value={t('locales.' + session.locale)}
                    />
                    {session.requiredMediaSource &&
                      session.requiredMediaSource.length > 0 && (
                        <SessionReviewSection.Element
                          label={t('sessions.display.requiredMediaSourceLabel')}
                        >
                          {session.requiredMediaSource.map((mediaSource) => (
                            <Chip color="blue">
                              {t(
                                `sessions.display.requiredMediaSourceChoices.${mediaSource}`,
                              )}
                            </Chip>
                          ))}
                        </SessionReviewSection.Element>
                      )}
                    {session.allowedDevicesCategories &&
                      session.allowedDevicesCategories.length > 0 && (
                        <SessionReviewSection.Element
                          label={t('sessions.display.allowedDevicesLabel')}
                        >
                          {session.allowedDevicesCategories.map(
                            (allowedDevicesCategory) => (
                              <Chip color="blue">
                                {t(
                                  `sessions.display.allowedDevicesCategoriesChoices.${allowedDevicesCategory}`,
                                )}
                              </Chip>
                            ),
                          )}
                        </SessionReviewSection.Element>
                      )}
                  </SessionReviewSection>
                  <SessionReviewSection
                    step={EditSessionStep.TARGETING}
                    session={session}
                  >
                    {offer && offer.color && (
                      <SessionReviewSection.Element
                        label={t('sessions.display.offer')}
                      >
                        <Chip color={offer.color as Colors}>{offer.name}</Chip>
                      </SessionReviewSection.Element>
                    )}
                    {fieldCriteria.map(
                      (criterion) =>
                        !!participantFieldsByName[criterion.fieldRef] && (
                          <SessionReviewSection.Element
                            label={
                              participantFieldsByName[criterion.fieldRef].label
                            }
                            value={getParticpantFieldCriterionTextValue(
                              criterion,
                              participantFieldsByName[criterion.fieldRef],
                            )}
                          />
                        ),
                    )}
                    {!fieldCriteria.length && (
                      <div className={classes.empty}>
                        <Trans i18nKey="criteria.empty" />
                      </div>
                    )}
                  </SessionReviewSection>
                  <SessionReviewSection
                    step={EditSessionStep.SCREENING_FORM}
                    session={session}
                  >
                    {recruitment && (
                      <SessionReviewSection.Element
                        label={
                          !recruitment.automaticValidation
                            ? t(
                                'editSession.screening.profileValidation.manuallyChoice',
                              )
                            : t(
                                'editSession.screening.profileValidation.automaticChoice',
                              )
                        }
                        value={
                          !recruitment.automaticValidation
                            ? t(
                                'editSession.screening.profileValidation.manuallyDetails',
                              )
                            : t(
                                'editSession.screening.profileValidation.automaticDetails',
                              )
                        }
                      />
                    )}
                    <SessionReviewSection.Element
                      label={t('editSession.review.screener.title')}
                    >
                      <Trans
                        i18nKey="editSession.review.screener.questionCount"
                        values={{ count: questionCount }}
                      />
                    </SessionReviewSection.Element>
                  </SessionReviewSection>
                  <SessionReviewSection
                    step={EditSessionStep.SCHEDULE}
                    session={session}
                  >
                    <SessionReviewSection.Element
                      label={t(
                        'editSession.review.schedule.availableTimeSlots',
                      )}
                    />
                    {session.startDate && session.endDate && (
                      <>
                        <SessionReviewSection.Element
                          label={t(
                            'editSession.review.schedule.firstTimeSlots',
                          )}
                          value={moment(session.startDate).format('LL LTS')}
                        />
                        <SessionReviewSection.Element
                          label={t('editSession.review.schedule.lastTimeSlots')}
                          value={moment(session.endDate).format('LL LTS')}
                        />
                      </>
                    )}
                    <SessionReviewSection.Element
                      label={t('editSession.review.schedule.buffer')}
                    >
                      <Trans
                        i18nKey="editSession.review.schedule.bufferTime"
                        values={{
                          count: moderatedTaskSettings?.appointmentBuffer,
                        }}
                      />
                    </SessionReviewSection.Element>
                  </SessionReviewSection>
                </div>
              )}
            </SessionFormSection>

            <SessionReviewSection
              step={EditSessionStep.LINK_CONFIGURATION}
              session={session}
            >
              {unmoderatedTaskSettings?.studyToolLinks?.map((link) => (
                <SessionReviewSection.Element
                  label={t('sessions.display.studyLink')}
                  value={link.studyToolLink}
                />
              ))}
              <SessionReviewSection.Element
                label={t('sessions.display.linkInformations')}
                value={unmoderatedTaskSettings?.studyToolInstructions}
              />
            </SessionReviewSection>
          </div>
          {plan && (
            <div className={classes.planColumn}>
              <ReviewPlanCard
                plan={plan}
                session={session}
                className={classes.planCard}
                isBillingInformationRequired={isBillingInformationRequired}
              >
                {showConsent && (
                  <div className={classes.checkbox}>
                    <ConsentCheckbox
                      consent={ConsentName.CLIENT_STUDY_TERMS}
                      value={consents}
                      onChange={setConsents}
                      alwaysShow
                      error={
                        !consents && submitted
                          ? t('form.errors.cgu.invalid')
                          : undefined
                      }
                      noMargin
                    />
                  </div>
                )}
              </ReviewPlanCard>
            </div>
          )}
        </div>
      </EditSessionLayout>
    </form>
  );
};
